import { ItemDetails } from 'modules/creativeLibrary';
import { getHtmlImageTagSrc } from 'modules/dynamicContentBuilder/utils';
import styled from 'styled-components';
import { EmptyAppState } from 'ui/illustrations';
import { Skeleton } from 'ui/skeleton';
import { useContentWatch } from '../../../hooks/form';
import { useTranslation } from '../../../hooks/useTranslation';
import { useImage } from '../hooks/useImage';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.values.half};
  grid-area: center;
`;

const ImageWrapper = styled.div`
  grid-area: center;
  padding: ${({ theme }) => theme.spacing.values.threeQuarter};
  background-color: #f3f4f5;
  border: ${({ theme }) => theme.spacing.values.single} solid #c3c3c5;
  border-radius: 4px;
  height: ${({ theme }) => theme.spacing.values.x16};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ImageName = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
  font-weight: 500;
`;

const MetadataList = styled.ul`
  display: flex;
  justify-content: start;
  margin: 0;
  padding: 0;
  gap: ${({ theme }) => theme.spacing.values.half};
`;

const MetadataItem = styled.li`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
  &::after {
    content: '•';
    margin-left: ${({ theme }) => theme.spacing.values.half};
    color: #c3c3c5;
  }

  &:last-of-type::after {
    content: '';
    margin: 0;
  }
`;

const Image = styled.img`
  max-height: 100%;
  max-width: 100%;
  border-radius: 3px;
`;

const Error = styled.div`
  margin-top: -${({ theme }) => theme.spacing.values.double};
`;

const ErrorTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
`;

const ErrorDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
`;

export const ImagePreview = () => {
  const { t } = useTranslation();
  const content = useContentWatch() ?? '';
  const imageSrc = getHtmlImageTagSrc(content);
  const { didError, isLoading, imgMetadata } = useImage(imageSrc);

  const loadingSkeleton = isLoading && <Skeleton />;

  const canShowPreview = !!imageSrc && !isLoading && !didError;
  const preview = canShowPreview && (
    <Image data-test="image-preview--image" src={imageSrc || undefined} />
  );

  const shouldShowImageDetails = !didError && !!Object.keys(imgMetadata).length;
  const shouldShowImageMetadata = !isLoading;
  const imageDetails = shouldShowImageDetails && (
    <div>
      <ImageName>{imgMetadata.assetName}</ImageName>
      {shouldShowImageMetadata && (
        <MetadataList data-test="image-preview--metadata">
          <MetadataItem>
            <ItemDetails metadata={imgMetadata} />
          </MetadataItem>
        </MetadataList>
      )}
    </div>
  );

  const shouldShowError = !isLoading && didError;
  const errorMessage = shouldShowError && (
    <Error data-test="image-preview--error">
      <EmptyAppState />
      <ErrorTitle>{t('contentPicker.imageLoadingErrorTitle')}</ErrorTitle>
      <ErrorDescription>{t('contentPicker.imageLoadingErrorDescription')}</ErrorDescription>
    </Error>
  );

  return (
    <Wrapper data-test="image-preview">
      <ImageWrapper>
        {preview}
        {errorMessage}
        {loadingSkeleton}
      </ImageWrapper>
      {imageDetails}
    </Wrapper>
  );
};
